import React, { useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { connect } from "react-redux";
import moment from "moment";
import DashboardDatePicker from "../DashboardDatePicker";
import ChartViewToggle from "../ChartViewToggle";
import ChartCard, { ChartTypeToggle, FilterSelect, ResetButton } from "../ChartCard";
import useChartRequest from "../useChartRequest";
import { PALETTE, cartesianOptions, formatMoney, isAllZero, seriesDataset } from "../chartTheme";
import { sendPostRequest } from "../../../../../actions/app-actions";
import { getAdminBusinessId } from "../../../../../utils/authHelper";

const SalesStatesChart = (props) => {
  const { rows, loading, refreshing, error, load } = useChartRequest(
    props.sendPostRequest,
    `/admin/event-daily-sales-chart/${getAdminBusinessId()}`
  );
  const [chartType, setChartType] = useState("bar");
  const [period, setPeriod] = useState(new Date());
  const [isMonthly, setIsMonthly] = useState(false);
  const [eventId, setEventId] = useState("");

  const request = (next = {}) => {
    const p = next.period !== undefined ? next.period : period;
    const monthly = next.isMonthly !== undefined ? next.isMonthly : isMonthly;
    const ev = next.eventId !== undefined ? next.eventId : eventId;
    load({
      event_id: ev,
      is_month_view: monthly ? "E" : "D",
      month: moment(p).format("MM"),
      year: moment(p).format("YYYY"),
    });
  };

  useEffect(() => {
    request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const values = rows.map((r) => Number(r.total) || 0);
  const total = values.reduce((a, b) => a + b, 0);
  const average = values.length ? total / values.length : 0;

  const chartData = useMemo(
    () => ({
      labels: rows.map((r) => r.index),
      datasets: [
        seriesDataset({
          label: "Sales",
          data: rows.map((r) => r.total),
          color: PALETTE.success,
          type: chartType,
          fill: true,
        }),
      ],
    }),
    [rows, chartType]
  );

  const options = useMemo(() => cartesianOptions({ money: true }), []);
  const periodLabel = isMonthly ? moment(period).format("YYYY") : moment(period).format("MMMM YYYY");

  const actions = (
    <>
      <FilterSelect
        value={eventId}
        options={props.events}
        placeholder="All events"
        onChange={(v) => {
          setEventId(v);
          request({ eventId: v });
        }}
      />
      <DashboardDatePicker
        mode={isMonthly ? "year" : "month"}
        value={period}
        onChange={(val) => {
          const next = val || new Date();
          setPeriod(next);
          request({ period: next });
        }}
      />
      <ChartViewToggle
        leftLabel="Daily"
        rightLabel="Monthly"
        isRightActive={isMonthly}
        onChange={(monthly) => {
          setIsMonthly(monthly);
          request({ isMonthly: monthly });
        }}
      />
      <ChartTypeToggle value={chartType} onChange={setChartType} />
      <ResetButton
        onClick={() => {
          const now = new Date();
          setEventId("");
          setIsMonthly(false);
          setPeriod(now);
          request({ eventId: "", isMonthly: false, period: now });
        }}
      />
    </>
  );

  const summary =
    !loading && !error ? (
      <div className="dash-figure">
        <span className="dash-figure__value">{formatMoney(total)}</span>
        <span className="dash-figure__meta">
          {periodLabel} · {isMonthly ? "Monthly" : "Daily"} average {formatMoney(average)}
        </span>
      </div>
    ) : null;

  return (
    <ChartCard
      title="Event sales"
      subtitle={`${isMonthly ? "Monthly" : "Daily"} net sales${eventId ? " for the selected event" : " across all events"}`}
      tooltipCode="P3F9"
      actions={actions}
      summary={summary}
      loading={loading}
      refreshing={refreshing}
      error={error}
      empty={isAllZero(values)}
      emptyTitle="No sales in this period"
      height={260}
    >
      {chartType === "bar" ? (
        <Bar data={chartData} options={options} />
      ) : (
        <Line data={chartData} options={options} />
      )}
    </ChartCard>
  );
};

export default connect(null, { sendPostRequest })(SalesStatesChart);
